import React from 'react';
import CourseTemplate from '../../components/CourseTemplate';

const MBBSCoursePage: React.FC = () => {
  const mbbsData = {
    title: "Bachelor of Medicine, Bachelor of Surgery",
    shortName: "MBBS",
    duration: "5.5 Years (including 1 year internship)",
    description: "The premier medical degree program that trains students to become qualified medical doctors capable of diagnosing, treating, and preventing diseases.",
    overview: "MBBS is the most sought-after undergraduate medical degree that provides comprehensive medical education combining theoretical knowledge with practical clinical experience. The program covers all aspects of human anatomy, physiology, pathology, pharmacology, and clinical medicine. Students learn to diagnose and treat various medical conditions, perform medical procedures, and develop critical thinking skills essential for medical practice. The curriculum is designed to produce competent medical professionals who can serve in hospitals, clinics, research institutions, and public health organizations.",
    eligibility: [
      "Completed 10+2 with Physics, Chemistry, Biology, and English",
      "Minimum 50% aggregate marks (40% for SC/ST/OBC)",
      "Must qualify NEET-UG entrance examination",
      "Age: 17-25 years (relaxation for reserved categories)",
      "Must be medically fit",
      "Indian citizenship (NRI quota available)"
    ],
    careerProspects: {
      roles: [
        "General Physician",
        "Specialist Doctor (after MD/MS)",
        "Medical Officer",
        "Emergency Medicine Physician",
        "Medical Consultant",
        "Hospital Administrator",
        "Medical Researcher",
        "Public Health Officer",
        "Medical Writer",
        "Forensic Medical Expert"
      ],
      industries: [
        "Government Hospitals",
        "Private Healthcare",
        "Medical Research",
        "Pharmaceutical Companies",
        "Medical Education",
        "Public Health Organizations",
        "Medical Tourism",
        "Telemedicine",
        "Healthcare Technology",
        "International Health Organizations"
      ],
      averageSalary: "₹6-25 LPA (varies by specialization and experience)",
      topRecruiters: [
        "AIIMS",
        "Apollo Hospitals",
        "Fortis Healthcare",
        "Max Healthcare",
        "Manipal Hospitals",
        "WHO",
        "MSF (Doctors Without Borders)",
        "Indian Armed Forces",
        "State Health Departments",
        "Private Clinics"
      ]
    },
    curriculum: [
      {
        year: 1,
        subjects: [
          "Human Anatomy",
          "Human Physiology",
          "Biochemistry",
          "Foundation Course in Medical Education",
          "Sports and Yoga",
          "Language",
          "Early Clinical Exposure"
        ]
      },
      {
        year: 2,
        subjects: [
          "Pathology",
          "Pharmacology",
          "Microbiology",
          "Forensic Medicine",
          "Community Medicine",
          "Clinical Postings",
          "Research Methodology"
        ]
      },
      {
        year: 3,
        subjects: [
          "General Medicine",
          "General Surgery",
          "Obstetrics & Gynecology",
          "Pediatrics",
          "Orthopedics",
          "ENT",
          "Ophthalmology",
          "Psychiatry"
        ]
      },
      {
        year: 4,
        subjects: [
          "Internal Medicine",
          "Surgery",
          "Obstetrics & Gynecology",
          "Pediatrics",
          "Community Medicine",
          "Clinical Rotations",
          "Emergency Medicine",
          "Anesthesiology"
        ]
      },
      {
        year: 5,
        subjects: [
          "Clinical Rotations",
          "Elective Postings",
          "Rural Health Training",
          "Dissertation/Research Project",
          "Comprehensive Examinations",
          "Pre-internship Training"
        ]
      }
    ],
    admissionProcess: [
      "Appear for NEET-UG examination",
      "Register for counseling (NEET counseling)",
      "Choice filling and locking",
      "Seat allotment based on rank and preferences",
      "Document verification",
      "Fee payment and admission confirmation",
      "Medical fitness certificate",
      "Join the allotted college"
    ],
    topColleges: [
      {
        name: "AIIMS New Delhi",
        location: "New Delhi",
        fees: "₹5,856 (Total)",
        ranking: "1st in India"
      },
      {
        name: "AIIMS Bombay",
        location: "Mumbai",
        fees: "₹5,856 (Total)",
        ranking: "2nd in India"
      },
      {
        name: "JIPMER",
        location: "Puducherry",
        fees: "₹4,684 (Total)",
        ranking: "3rd in India"
      },
      {
        name: "CMC Vellore",
        location: "Vellore, Tamil Nadu",
        fees: "₹8.5 LPA",
        ranking: "4th in India"
      },
      {
        name: "KGMC",
        location: "Lucknow",
        fees: "₹1.2 LPA",
        ranking: "5th in India"
      },
      {
        name: "Stanley Medical College",
        location: "Chennai, Tamil Nadu",
        fees: "₹50,000 PA",
        ranking: "Top in Tamil Nadu"
      },
      {
        name: "Madras Medical College",
        location: "Chennai, Tamil Nadu",
        fees: "₹45,000 PA",
        ranking: "Premier Government College"
      }
    ],
    skills: [
      "Clinical Diagnosis",
      "Patient Care",
      "Medical Ethics",
      "Communication Skills",
      "Critical Thinking",
      "Emergency Management",
      "Surgical Skills",
      "Research Methodology",
      "Leadership",
      "Empathy",
      "Time Management",
      "Medical Documentation"
    ],
    futureScope: "The medical field offers unlimited growth opportunities with specializations in over 40+ branches like Cardiology, Neurology, Oncology, Radiology, and more. With advancing medical technology, telemedicine, AI in healthcare, and medical research, MBBS graduates have opportunities in emerging fields like precision medicine, medical AI, biotechnology, and global health. The demand for healthcare professionals continues to grow exponentially, especially post-pandemic, ensuring excellent career prospects both in India and internationally.",
    faqs: [
      {
        question: "What is the minimum NEET score required for MBBS admission?",
        answer: "The minimum NEET score varies each year based on the difficulty level and number of candidates. Generally, a score above 600+ is considered competitive for government colleges, while 550+ might get you admission in private colleges. For reserved categories, the cut-off is typically 100-150 points lower."
      },
      {
        question: "Can I pursue MBBS without NEET?",
        answer: "No, NEET-UG is mandatory for all MBBS admissions in India, including government and private colleges. Some deemed universities previously had their own entrance tests, but now all must follow NEET."
      },
      {
        question: "What are the career options after MBBS?",
        answer: "After MBBS, you can practice as a general physician, pursue MD/MS for specialization, join government services like Indian Army Medical Corps, work in private hospitals, start your own clinic, or explore research and teaching opportunities."
      },
      {
        question: "Is MBBS a good career option financially?",
        answer: "Yes, MBBS offers excellent financial prospects. Fresh graduates can earn ₹6-15 LPA, while specialists can earn ₹25+ LPA. Private practice and specialized fields can offer even higher income potential."
      },
      {
        question: "How difficult is the MBBS course?",
        answer: "MBBS is academically challenging with extensive curriculum and long hours of study. However, with dedication, proper time management, and consistent effort, students can successfully complete the course. The practical experience makes the learning rewarding."
      },
      {
        question: "Can I pursue MBBS abroad?",
        answer: "Yes, many Indian students pursue MBBS abroad in countries like Russia, Ukraine, Philippines, China, etc. However, you need to clear FMGE (Foreign Medical Graduate Examination) to practice in India after returning."
      }
    ]
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0f172a] via-[#1e293b] to-black flex flex-col items-center justify-start py-8 px-2">
      <h1 className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-400 to-purple-400 drop-shadow-glow mb-6 text-center animate-glow">
        {mbbsData.title} ({mbbsData.shortName})
      </h1>
      <div className="w-full max-w-4xl">
        <CourseTemplate courseData={mbbsData} />
      </div>
    </div>
  );
};

export default MBBSCoursePage;
